if (!customElements.get('pm-platform-marquee')) {
  const INTERACTIVE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex], [contenteditable]';
  // PM 05-10-2026: arrastre con mouse / deslizamiento táctil.
  const DRAG_THRESHOLD = 6; // px antes de considerar que es arrastre (y no clic/toque)
  const INERTIA_DECAY = 0.95; // por cada ~16 ms
  const INERTIA_MIN = 0.02; // px/ms bajo el cual la inercia se detiene

  function stripEditorArtifacts(root) {
    const nodes = [root, ...root.querySelectorAll('*')];
    nodes.forEach((el) => {
      if (el.hasAttribute('id')) el.removeAttribute('id');
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.indexOf('data-shopify-') === 0) {
          el.removeAttribute(attr.name);
        }
      });
    });
  }

  // Normaliza el desplazamiento al rango (-g, 0] para el bucle infinito.
  function wrapOffset(x, g) {
    if (!g) return 0;
    let r = x % g;
    if (r > 0) r -= g;
    return r;
  }

  class PmPlatformMarquee extends HTMLElement {
    connectedCallback() {
      if (this._resizeObserver) return;

      this.viewport = this.querySelector('.pm-platform-marquee__viewport');
      this.track = this.querySelector('.pm-platform-marquee__track');
      if (!this.viewport || !this.track) return;

      this.baseGroup = this.track.querySelector('.pm-platform-marquee__group');
      if (!this.baseGroup) return;

      this._rafId = null;
      this._focusRafId = null;

      // Estado del movimiento por JS (reemplaza la animación CSS cuando está activo).
      this._groupWidth = 0;
      this._offset = 0;
      this._offsetReady = false;
      this._loopId = null;
      this._lastTime = 0;
      this._hovering = false;
      this._inView = true;
      this._velocity = 0;
      this._pointer = null;
      this._suppressClick = false;

      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onMotionChange = () => {
        if (this._motionQuery.matches) {
          if (this._rafId) {
            window.cancelAnimationFrame(this._rafId);
            this._rafId = null;
          }
          this.disableJsMotion();
          this.removeDynamicClones();
        } else {
          this.enableJsMotion();
          this.scheduleMeasure();
        }
      };
      this._motionQuery.addEventListener('change', this._onMotionChange);

      this._resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this.baseGroup);

      this._onFocusIn = (event) => this.handleFocusIn(event);
      this._onFocusOut = (event) => this.handleFocusOut(event);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      if (!this._motionQuery.matches) this.enableJsMotion();
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._motionQuery && this._onMotionChange) {
        this._motionQuery.removeEventListener('change', this._onMotionChange);
      }
      this._motionQuery = null;
      this._onMotionChange = null;

      if (this._onFocusIn) this.removeEventListener('focusin', this._onFocusIn);
      if (this._onFocusOut) this.removeEventListener('focusout', this._onFocusOut);
      this._onFocusIn = null;
      this._onFocusOut = null;

      if (this._rafId) {
        window.cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      if (this._focusRafId) {
        window.cancelAnimationFrame(this._focusRafId);
        this._focusRafId = null;
      }

      this.disableJsMotion();

      this.classList.remove('pm-platform-marquee--focus-active');
      if (this.viewport) this.viewport.scrollLeft = 0;

      this.removeDynamicClones();
    }

    /* ---------- Movimiento por JS + arrastre ---------- */

    enableJsMotion() {
      if (this._jsEnabled) return;
      this._jsEnabled = true;
      this.classList.add('pm-platform-marquee--js');

      this._onPointerDown = (e) => this.handlePointerDown(e);
      this._onPointerMove = (e) => this.handlePointerMove(e);
      this._onPointerUp = (e) => this.handlePointerUp(e, false);
      this._onPointerCancel = (e) => this.handlePointerUp(e, true);
      this._onPointerEnter = (e) => {
        if (e.pointerType === 'mouse') this._hovering = true;
      };
      this._onPointerLeave = (e) => {
        if (e.pointerType === 'mouse') this._hovering = false;
      };
      this._onClickCapture = (e) => {
        if (!this._suppressClick) return;
        this._suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      };
      this._onDragStart = (e) => e.preventDefault();

      this.viewport.addEventListener('pointerdown', this._onPointerDown);
      this.viewport.addEventListener('pointermove', this._onPointerMove);
      this.viewport.addEventListener('pointerup', this._onPointerUp);
      this.viewport.addEventListener('pointercancel', this._onPointerCancel);
      this.viewport.addEventListener('pointerenter', this._onPointerEnter);
      this.viewport.addEventListener('pointerleave', this._onPointerLeave);
      this.viewport.addEventListener('click', this._onClickCapture, true);
      this.viewport.addEventListener('dragstart', this._onDragStart);

      // Solo anima cuando la cinta está en pantalla (ahorra CPU/batería).
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          this._inView = entries.some((entry) => entry.isIntersecting);
          if (this._inView) this.startLoop();
        });
        this._io.observe(this);
      }

      this.startLoop();
    }

    disableJsMotion() {
      if (!this._jsEnabled) return;
      this._jsEnabled = false;
      this.classList.remove('pm-platform-marquee--js', 'pm-platform-marquee--dragging');

      if (this.viewport) {
        this.viewport.removeEventListener('pointerdown', this._onPointerDown);
        this.viewport.removeEventListener('pointermove', this._onPointerMove);
        this.viewport.removeEventListener('pointerup', this._onPointerUp);
        this.viewport.removeEventListener('pointercancel', this._onPointerCancel);
        this.viewport.removeEventListener('pointerenter', this._onPointerEnter);
        this.viewport.removeEventListener('pointerleave', this._onPointerLeave);
        this.viewport.removeEventListener('click', this._onClickCapture, true);
        this.viewport.removeEventListener('dragstart', this._onDragStart);
      }
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      this.stopLoop();
      this._pointer = null;
      this._velocity = 0;
      if (this.track) this.track.style.transform = '';
    }

    startLoop() {
      if (!this._jsEnabled || this._loopId) return;
      this._lastTime = 0;
      this._loopId = window.requestAnimationFrame((t) => this.tick(t));
    }

    stopLoop() {
      if (this._loopId) {
        window.cancelAnimationFrame(this._loopId);
        this._loopId = null;
      }
    }

    tick(time) {
      this._loopId = null;
      if (!this._jsEnabled) return;

      const dt = this._lastTime ? Math.min(time - this._lastTime, 64) : 0;
      this._lastTime = time;
      const g = this._groupWidth;

      if (g > 0 && !this.classList.contains('pm-platform-marquee--focus-active')) {
        if (!this._offsetReady) {
          this._offset = this.dataset.direction === 'right' ? -g : 0;
          this._offsetReady = true;
        }

        if (this._pointer && this._pointer.dragging) {
          // La posición la fija handlePointerMove.
        } else if (Math.abs(this._velocity) > INERTIA_MIN) {
          this._offset += this._velocity * dt;
          this._velocity *= Math.pow(INERTIA_DECAY, dt / 16);
        } else {
          this._velocity = 0;
          const pauseHover = this._hovering && this.classList.contains('pm-platform-marquee--pause-hover');
          if (!pauseHover) {
            const speed = parseFloat(this.dataset.speed) || 80; // px/s
            const dir = this.dataset.direction === 'right' ? 1 : -1;
            this._offset += (dir * speed * dt) / 1000;
          }
        }

        this._offset = wrapOffset(this._offset, g);
        this.track.style.transform = `translate3d(${this._offset}px, 0, 0)`;
      }

      if (this._inView || (this._pointer && this._pointer.dragging)) {
        this._loopId = window.requestAnimationFrame((t) => this.tick(t));
      }
    }

    handlePointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this._velocity = 0;
      this._pointer = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startOffset: this._offset,
        lastX: e.clientX,
        lastT: e.timeStamp,
        dragging: false,
      };
    }

    handlePointerMove(e) {
      const p = this._pointer;
      if (!p || p.id !== e.pointerId) return;

      const dx = e.clientX - p.startX;
      if (!p.dragging) {
        const dy = e.clientY - p.startY;
        if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
        p.dragging = true;
        this.classList.add('pm-platform-marquee--dragging');
        try { this.viewport.setPointerCapture(e.pointerId); } catch (err) {}
      }

      e.preventDefault();
      this._offset = wrapOffset(p.startOffset + dx, this._groupWidth);
      this.track.style.transform = `translate3d(${this._offset}px, 0, 0)`;

      const dtm = e.timeStamp - p.lastT;
      if (dtm > 0) {
        const v = (e.clientX - p.lastX) / dtm;
        this._velocity = this._velocity * 0.6 + v * 0.4; // suavizado
      }
      p.lastX = e.clientX;
      p.lastT = e.timeStamp;
    }

    handlePointerUp(e, cancelled) {
      const p = this._pointer;
      if (!p || p.id !== e.pointerId) return;
      this._pointer = null;

      if (p.dragging) {
        this.classList.remove('pm-platform-marquee--dragging');
        try { this.viewport.releasePointerCapture(e.pointerId); } catch (err) {}
        // Si el último movimiento fue hace rato, sin inercia.
        if (e.timeStamp - p.lastT > 80) this._velocity = 0;
        this._velocity = Math.max(-3, Math.min(3, this._velocity));
        if (!cancelled) {
          // Evita que el enlace de la marca se abra al soltar tras arrastrar.
          this._suppressClick = true;
          window.setTimeout(() => { this._suppressClick = false; }, 0);
        }
      } else {
        this._velocity = 0;
      }
      this.startLoop();
    }

    /* ---------- Medición y clones (sin cambios de lógica) ---------- */

    scheduleMeasure() {
      if (this._motionQuery && this._motionQuery.matches) return;

      if (this._rafId) window.cancelAnimationFrame(this._rafId);
      this._rafId = window.requestAnimationFrame(() => {
        this._rafId = null;
        this.measureAndClone();
      });
    }

    removeDynamicClones() {
      if (!this.track) return;
      this.track.querySelectorAll('[data-pm-marquee-clone]').forEach((clone) => clone.remove());
    }

    measureAndClone() {
      if (this._motionQuery && this._motionQuery.matches) return;

      this.removeDynamicClones();

      const containerWidth = this.getBoundingClientRect().width;
      const groupWidth = this.baseGroup.getBoundingClientRect().width;

      if (containerWidth === 0 || groupWidth === 0) {
        // Sin tamaño real todavía; el ResizeObserver disparará de nuevo en
        // cuanto la instancia o el primer grupo obtengan un tamaño válido.
        return;
      }

      const speed = parseFloat(this.dataset.speed) || 80;
      this.style.setProperty('--pm-marquee-distance', `${groupWidth}px`);
      this.style.setProperty('--pm-marquee-duration', `${groupWidth / speed}s`);

      // Movimiento por JS: conserva la posición proporcional si cambia el ancho.
      if (this._groupWidth && this._offsetReady) {
        this._offset = (this._offset / this._groupWidth) * groupWidth;
      }
      this._groupWidth = groupWidth;

      const requiredWidth = containerWidth + groupWidth;
      const baseGroupCount = this.track.querySelectorAll('.pm-platform-marquee__group').length;
      let currentWidth = groupWidth * baseGroupCount;

      while (currentWidth < requiredWidth) {
        const clone = this.baseGroup.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-pm-marquee-clone', '');

        clone.querySelectorAll(INTERACTIVE_SELECTOR).forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });

        stripEditorArtifacts(clone);

        this.track.appendChild(clone);
        currentWidth += groupWidth;
      }

      this.startLoop();
    }

    /* ---------- Foco de teclado (sin cambios) ---------- */

    handleFocusIn(event) {
      if (!this.classList.contains('pm-platform-marquee--pause-focus')) return;
      if (event.target.closest('[aria-hidden="true"]')) return;

      let isKeyboardFocus = true;
      try { isKeyboardFocus = event.target.matches(':focus-visible'); } catch (e) {}
      if (!isKeyboardFocus) return;

      this.classList.add('pm-platform-marquee--focus-active');
      this.scheduleFocusAlign(event.target);
    }

    handleFocusOut(event) {
      if (!this.classList.contains('pm-platform-marquee--pause-focus')) return;
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;

      this.classList.remove('pm-platform-marquee--focus-active');

      if (this._focusRafId) {
        window.cancelAnimationFrame(this._focusRafId);
        this._focusRafId = null;
      }
      if (this.viewport) this.viewport.scrollLeft = 0;
      this.startLoop();
    }

    scheduleFocusAlign(target) {
      if (this._focusRafId) window.cancelAnimationFrame(this._focusRafId);
      this._focusRafId = window.requestAnimationFrame(() => {
        this._focusRafId = null;
        this.alignFocusedElement(target);
      });
    }

    alignFocusedElement(target) {
      if (!this.viewport || !target || !this.viewport.contains(target)) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      if (targetRect.left < viewportRect.left) {
        this.viewport.scrollLeft -= viewportRect.left - targetRect.left;
      } else if (targetRect.right > viewportRect.right) {
        this.viewport.scrollLeft += targetRect.right - viewportRect.right;
      }
    }
  }

  customElements.define('pm-platform-marquee', PmPlatformMarquee);
}
